import { Box } from '@mui/system';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { ICompany } from 'app/shared/model/company.model';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useState } from 'react';
import { Translate, getSortState, translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { getAlertDataSummmary } from './alert.reducer';

export const Alert = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const { match } = props;
  const totalItems = useAppSelector(state => state.alert.totalItems);
  const loading = useAppSelector(state => state.alert.loading);
  const urlParams = new URLSearchParams(props.location.search);

  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const alertList = useAppSelector(state => state.alert.entities);
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'), props.location.search)
  );
  const [searchCompanyName, setSearchCompanyName] = useState<string>(urlParams.get('searchCompanyName') || '');
  const [nameSearch, setNameSearch] = useState<string>(urlParams.get('nameSearch') || '');
  const [searchCluster, setSearchCluster] = useState(urlParams.get('searchCluster') || '');
  const [status, setStatus] = useState(urlParams.get('status') || StatusFilter.ACTIVATED);

  const allAdmins = isAdmin || isRepresentativeAdmin || isManufacturerAdmin || isTransporterAdmin;
  const companyFilterAdmin = isAdmin || isRepresentativeAdmin || isManufacturerAdmin;

  const headers: IHeaderItem[] = [
    isAdmin && {
      key: 'name',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.company.home.titleSingle" />,
    },
    {
      key: 'name',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.name" />,
    },
    {
      key: 'alertType',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.alertType" />,
    },
    {
      key: 'sensor.name',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.sensor" />,
    },
    {
      key: 'alertOperator',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.alertOperator" />,
    },
    {
      key: 'alertCluster',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.alertCluster" />,
    },
    {
      key: 'value',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.value" />,
    },
    {
      key: 'valueInRangeMin',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.valueInRangeMin" />,
    },
    {
      key: 'valueInRangeMax',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.alert.valueInRangeMax" />,
    },
    {
      key: 'activated',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.company.status" />,
    },
  ];

  const filteredHeaders = !allAdmins ? headers.filter(header => header.key !== 'type' && header.key !== 'activated') : headers;

  const data: IListData[] = [
    isAdmin && { type: 'text', value: 'companyName', onlyAdmin: isAdmin },
    { type: 'text', value: 'name' },
    { type: 'translateText', value: 'alertType', translate: 'fleetSenseApp.AlertType' },
    { type: 'text', value: 'sensorName' },
    { type: 'translateText', value: 'alertOperator', translate: 'fleetSenseApp.AlertOperator' },
    { type: 'translateText', value: 'alertCluster', translate: 'fleetSenseApp.AlertCluster' },
    { type: 'text', value: 'value' },
    { type: 'text', value: 'valueInRangeMin' },
    { type: 'text', value: 'valueInRangeMax' },
    { type: 'stateButton', value: 'activated' },

    {
      type: 'editButton',
      value: 'id',
      onlyAdmin: isAdmin || isRepresentativeAdmin || isManufacturerAdmin,
    },
  ];
  const handleSearchCompany = (company: ICompany) => {
    setSearchCompanyName(company?.name);
  };

  const sortClear = () => {
    setSearchCompanyName('');
    setSearchCluster('');
    setNameSearch('');
    setStatus(StatusFilter.ACTIVATED);
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  const searchTypes: ISearchTypes[] = [
    {
      label: translate('fleetSenseApp.alert.searchLabel'),
      placeholder: translate('fleetSenseApp.alert.search'),
      value: nameSearch,
      setValue: setNameSearch,
    },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(
      getAlertDataSummmary({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.order}`,
        searchName: nameSearch,
        searchCompanyName,
        searchCluster,
        status,
      })
    );
  }, [paginationState, status, nameSearch, searchCompanyName, searchCluster]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  useEffect(() => {
    const searchParams = [];

    if (nameSearch !== '') {
      searchParams.push(`nameSearch=${encodeURIComponent(nameSearch)}`);
    }
    if (searchCompanyName !== '') {
      searchParams.push(`searchCompanyName=${encodeURIComponent(searchCompanyName)}`);
    }
    if (searchCluster && searchCluster !== '') {
      searchParams.push(`searchCluster=${encodeURIComponent(searchCluster)}`);
    }

    if (status !== StatusFilter.ACTIVATED) {
      searchParams.push(`status=${encodeURIComponent(status)}`);
    }

    const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;

    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  }, [paginationState, searchCompanyName, searchCluster, status]);

  const handlePagination = (_, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  return (
    <>
      <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'start', padding: '24px' }}>
        <PageHeader
          headingId="alert-heading"
          headingDataCy="AlertHeading"
          contentKey={'fleetSenseApp.alert.home.title'}
          buttonTextKey={'fleetSenseApp.alert.create'}
          match={match}
          showButton={allAdmins}
        />

        <PageFilters
          setActivePage={setActivePage}
          isAdmin={companyFilterAdmin}
          searchTypes={searchTypes}
          status={status}
          setStatus={setStatus}
          getAllEntities={getAllEntities}
          handleSearchCompany={handleSearchCompany}
          searchCompanyName={searchCompanyName}
          sortClear={sortClear}
          searchCluster={searchCluster}
          setSearchCluster={setSearchCluster}
          showFilters={['filters', 'company', 'clearFilters', 'status', 'alertCluster']}
        />

        {!loading && (
          <TableComponent listData={data} headers={filteredHeaders} list={alertList.filter(Boolean)} match={match} sortFunction={sort} pagination={paginationState} />
        )}
        {!loading && !alertList.length && <NoData title="Alertas" />}
        {loading && <Loading />}
        <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={alertList} />
      </Box>
    </>
  );
};

export default Alert;
